/* eslint-disable max-lines */
import dayjs from "dayjs";
import { FC, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SourceBadge from "../../components/customer-feedback/SourceBadge";
import StatusBadge from "../../components/customer-feedback/StatusBadge";
import UpdateStatusModal from "../../components/customer-feedback/UpdateStatusModal";
import Back from "../../components/ui/Back";
import Button from "../../components/ui/Button";
import SpinnerLoader from "../../components/ui/SpinnerLoader";
import SuperAdmin from "../../components/users/permissions/SuperAdmin";
import {
  FeedbackStatus,
  ICustomerFeedback
} from "../../interfaces/endpoints/ICustomerFeedback";
import { getFeedbackById } from "../../services/CustomerFeedback.service";
import { formatRouteName } from "../../utils/customerFeedback/labels";

// Detail view for a single feedback record. Read-only except for
// the Status field, which is editable through the same modal used
// in the list table.
const CustomerFeedbackDetail: FC = () => {
  const { id } = useParams<{ id: string }>();
  const [record, setRecord] = useState<ICustomerFeedback | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getFeedbackById(parseInt(id, 10))
      .then((res) => {
        setRecord(res.data as unknown as ICustomerFeedback);
        setError(null);
      })
      .catch(() => {
        setError("Ky regjistrim nuk u ngarkua dot.");
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <SpinnerLoader />
      </div>
    );
  }

  if (error || !record) {
    return (
      <div className="bg-white rounded-lg border p-6">
        <p className="text-sm text-red-600 mb-4">
          {error ?? "Regjistrimi nuk u gjet."}
        </p>
        <Back to="/customer-feedback" title="Kthehu te lista" />
      </div>
    );
  }

  const handleStatusUpdated = (newStatus: FeedbackStatus) => {
    setRecord({ ...record, status: newStatus });
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between mb-4 gap-2">
        <Back
          to="/customer-feedback"
          title="Kthehu te lista"
          state={["Customer Feedback", "Detajet"]}
        />
        <SuperAdmin>
          <Button onClick={() => setStatusModalOpen(true)}>
            Ndrysho statusin
          </Button>
        </SuperAdmin>
      </div>

      <div className="bg-white rounded-lg border">
        <div className="px-6 py-4 border-b flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold text-tableColor">
              Reagim #{record.id}
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              {record.auditAuthor?.insertDate
                ? `Dërguar më ${dayjs(record.auditAuthor.insertDate).format(
                    "DD MMM YYYY · HH:mm"
                  )}`
                : "—"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <SourceBadge source={record.source} />
            <StatusBadge status={record.status} />
          </div>
        </div>

        <div className="px-6 py-5 space-y-6">
          <Section title="Informacione të përgjithshme">
            <dl className="grid grid-cols-1 sm:grid-cols-3 gap-y-4 gap-x-6">
              <Field label="Dërguar më">
                {record.auditAuthor?.insertDate
                  ? dayjs(record.auditAuthor.insertDate).format(
                      "DD MMM YYYY · HH:mm"
                    )
                  : "-"}
              </Field>
              <Field label="Përditësuar së fundmi">
                {record.auditAuthor?.updateDate ? (
                  <>
                    {dayjs(record.auditAuthor.updateDate).format(
                      "DD MMM YYYY · HH:mm"
                    )}
                    {record.auditAuthor.updatedByName ? (
                      <span className="text-gray-500">
                        {" · nga "}
                        {record.auditAuthor.updatedByName}
                        {record.auditAuthor.updatedByLastName
                          ? ` ${record.auditAuthor.updatedByLastName}`
                          : ""}
                      </span>
                    ) : null}
                  </>
                ) : (
                  "-"
                )}
              </Field>
              <Field label="ID e udhëtimit">
                {record.tripId !== null && record.tripId !== undefined ? (
                  <Link
                    to={`/trips/edit/${record.tripId}`}
                    className="text-primary hover:underline"
                  >
                    {record.tripId}
                  </Link>
                ) : (
                  "-"
                )}
              </Field>
              <Field label="Email">
                {record.email ? (
                  <a
                    href={`mailto:${record.email}`}
                    className="text-primary hover:underline"
                  >
                    {record.email}
                  </a>
                ) : (
                  <span className="italic text-gray-400">anonim</span>
                )}
              </Field>
              <Field label="Data e kërkimit">
                {record.searchDate || "-"}
              </Field>
              <Field label="Numri i rezultateve">
                {record.resultsCount !== null ? record.resultsCount : "-"}
              </Field>
            </dl>
          </Section>

          <Section title="Linja">
            <dl className="grid grid-cols-1 sm:grid-cols-3 gap-y-4 gap-x-6">
              <Field label="Linja" className="sm:col-span-3">
                <span className="font-medium">
                  {formatRouteName(record.routeOrigin)}
                </span>
                <span className="mx-2 text-gray-400">→</span>
                <span className="font-medium">
                  {formatRouteName(record.routeDestination)}
                </span>
              </Field>
              <Field label="ID e nisjes (lokacion)">
                {record.fromLocationId !== null && record.fromLocationId !== undefined
                  ? record.fromLocationId
                  : "-"}
              </Field>
              <Field label="ID e nisjes (stacion)">
                {record.fromStationId !== null && record.fromStationId !== undefined
                  ? record.fromStationId
                  : "-"}
              </Field>
              <Field label="ID e mbërritjes (lokacion)">
                {record.toLocationId !== null && record.toLocationId !== undefined
                  ? record.toLocationId
                  : "-"}
              </Field>
              <Field label="ID e mbërritjes (stacion)">
                {record.toStationId !== null && record.toStationId !== undefined
                  ? record.toStationId
                  : "-"}
              </Field>
            </dl>
          </Section>

          <Section title="Detajet e udhëtimit">
            <dl className="grid grid-cols-1 sm:grid-cols-3 gap-y-4 gap-x-6">
              <Field label="Operatori">
                {record.operatorName || "-"}
              </Field>
              <Field label="Ora e nisjes">
                {record.tripDepartureTime || "-"}
              </Field>
              <Field label="Ora e mbërritjes">
                {record.tripArrivalTime || "-"}
              </Field>
              <Field label="Tipi i linjës" className="sm:col-span-3">
                {record.lineType || "-"}
              </Field>
            </dl>
          </Section>

          <Section title="Përmbajtja">
            <dl className="grid grid-cols-1 gap-y-4">
              <Field label="Arsyet">
                {record.reasons && record.reasons.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {record.reasons.map((r, i) => (
                      <span
                        key={`${r}-${i}`}
                        className="inline-block rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-700"
                      >
                        {r}
                      </span>
                    ))}
                  </div>
                ) : (
                  "-"
                )}
              </Field>
              <Field label="Mesazhi">
                {record.message ? (
                  <p className="whitespace-pre-wrap text-sm text-tableColor leading-relaxed">
                    {record.message}
                  </p>
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </Field>
            </dl>
          </Section>
        </div>
      </div>

      <UpdateStatusModal
        show={statusModalOpen}
        feedbackId={record.id}
        currentStatus={record.status}
        onClose={() => setStatusModalOpen(false)}
        onUpdated={handleStatusUpdated}
      />
    </>
  );
};

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

const Section: FC<SectionProps> = ({ title, children }) => (
  <section>
    <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
      {title}
    </h2>
    {children}
  </section>
);

interface FieldProps {
  label: string;
  className?: string;
  children: React.ReactNode;
}

const Field: FC<FieldProps> = ({ label, className, children }) => (
  <div className={className}>
    <dt className="text-xs font-medium text-gray-500 mb-1">{label}</dt>
    <dd className="text-sm text-tableColor">{children}</dd>
  </div>
);

export default CustomerFeedbackDetail;
